import { supabase } from '@/lib/supabase';
import { BOT_USERNAME } from '@/utils/cardCanvas';
import {
  tg, inviteLink, uploadCard, shareCardWithFriend, shareImageFile, downloadImage,
} from '@/utils/shareCard';
import type { MicroChip } from '@/utils/mealMicros';

// ════════════════════════════════════════════════════════════════
// Карточка распознавания: фото тарелки + КБЖУ + «посчитано за N секунд».
//
// Зачем отдельно от карточек достижений: все они начинаются с 7 дней подряд,
// а восторг у человека — на ПЕРВОЙ МИНУТЕ, когда он навёл камеру на тарелку
// и получил разбор. Тот, кто способен привести друзей, — это он, сейчас,
// а показать ему нечего.
//
// И это лучшая реклама из возможных: не бейдж со счётчиком, а сам продукт
// в действии. Человек, который увидит такую картинку, поймёт, что покупает.
// ════════════════════════════════════════════════════════════════

const W = 1080;
// 9:16 под сторис (решение Славы 04.09.2026). В формате 4:5 Telegram
// добавлял чёрные полосы сверху и снизу — карточка выглядела вставленной,
// а не снятой. Сторис — основной канал, под него и верстаем.
const H = 1920;
// Telegram закрывает верх и низ сторис своим интерфейсом: аватар с именем
// сверху, поле ответа снизу. Содержимое держим в середине, а не растягиваем
// на всю высоту — иначе ссылка уедет под поле ввода.
const SAFE_TOP = 260;
const BG = '#0B0F12';
const EMERALD = '#10E58E';
const WARN = '#FFB454';
const SKY = '#3DD8FF';
const INK = '#F5F7FA';
const INK_MUTED = '#8B98A5';
const INK_DIM = '#5A6573';
const PAD = 120;

export type RecognitionFacts = {
  /** dataURL снимка — тот же, что показан на экране */
  photo: string | null;
  /** Витамины и минералы. Появляются только ПОСЛЕ сохранения — см. mealMicros.ts */
  micros?: MicroChip[];
  /** Состав приёма через запятую. Заменяет фото на карточке из Дневника. */
  composition?: string;
  title: string;
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  /** Сколько секунд занял разбор — цифра, которая и продаёт */
  seconds?: number | null;
};

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Разбивает строку по словам под ширину — для состава приёма под витаминами */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(next).width > maxW && cur) {
      lines.push(cur);
      cur = w;
    } else cur = next;
  }
  if (cur) lines.push(cur);
  return lines;
}

export async function renderRecognitionCard(
  f: RecognitionFacts,
  opts: { name?: string | null; refCode?: string | null } = {},
): Promise<Blob> {
  if (document.fonts?.ready) await document.fonts.ready;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);
  const g1 = ctx.createRadialGradient(W * 0.2, H * 0.1, 0, W * 0.2, H * 0.1, W * 1.2);
  g1.addColorStop(0, 'rgba(16,229,142,0.07)');
  g1.addColorStop(1, 'transparent');
  ctx.fillStyle = g1; ctx.fillRect(0, 0, W, H);

  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(16,229,142,0.75)';
  ctx.font = '500 26px Inter, system-ui, sans-serif';
  ctx.letterSpacing = '8px';
  ctx.fillText('SLAVA HEALTH', W / 2, SAFE_TOP);
  ctx.letterSpacing = '0px';

  // ── Фото тарелки: квадрат со скруглением, обрезка по центру ──
  //
  // ⚠️ Размер фото ЗАДАЁТ всю раскладку. Первая версия брала фото во всю
  // ширину (840px), и низ карточки не помещался: подвал рисовался «от низа»,
  // а БЖУ «от верха», они пересеклись, и граммы ушли за край холста на 20px.
  // Теперь всё считается одним потоком сверху вниз, а фото уменьшено так,
  // чтобы под ним хватило места на название, калории, БЖУ и подвал.
  const IMG_Y = SAFE_TOP + 60;
  // 760 → 680 (05.09.2026): освобождены 80px под строку разбора КБЖУ.
  // Запас до безопасной зоны сторис был всего 18px (подвал вставал на 1614
  // при границе 1632) — без этого разбор ушёл бы под интерфейс Telegram.
  // Посчитано по пикселям: фото низом на 1000, разбор на 1442, ссылка на 1600.
  const IMG_S = 680;
  const IMG_X = (W - IMG_S) / 2;
  // ⚠️ ДВЕ РАСКЛАДКИ, и это не украшательство.
  //
  // Фото еды мы намеренно НЕ храним (политика, редакция 2.0). Замерено
  // 05.09.2026: снимок есть у 17 приёмов из 4343, хранение включил 1 профиль.
  // Значит карточка из Дневника почти всегда без фото, а прежний код рисовал
  // на его месте пустой серый квадрат в 680px — на публичной картинке это брак.
  //
  // Без фото героем становятся ВИТАМИНЫ: они занимают освободившееся место
  // вертикальным списком на пять строк вместо одной, а под ними идёт состав
  // приёма. Такая карточка продаёт не «смотри, что я ем», а «смотри, что
  // приложение видит в еде» — и это единственное, чего нет у конкурентов.
  const hasPhoto = !!f.photo;
  if (hasPhoto) {
  ctx.save();
  roundRect(ctx, IMG_X, IMG_Y, IMG_S, IMG_S, 32);
  ctx.clip();
  if (f.photo) {
    try {
      const img = await loadImage(f.photo);
      const scale = Math.max(IMG_S / img.width, IMG_S / img.height);
      const dw = img.width * scale;
      const dh = img.height * scale;
      ctx.drawImage(img, IMG_X + (IMG_S - dw) / 2, IMG_Y + (IMG_S - dh) / 2, dw, dh);
    } catch {
      ctx.fillStyle = '#141A1F';
      ctx.fillRect(IMG_X, IMG_Y, IMG_S, IMG_S);
    }
  } else {
    ctx.fillStyle = '#141A1F';
    ctx.fillRect(IMG_X, IMG_Y, IMG_S, IMG_S);
  }
  ctx.restore();
  ctx.strokeStyle = 'rgba(16,229,142,0.28)';
  ctx.lineWidth = 2;
  roundRect(ctx, IMG_X, IMG_Y, IMG_S, IMG_S, 32);
  ctx.stroke();

  // ── Плашка «посчитано за N секунд» поверх нижнего края фото ──
  if (f.seconds && f.seconds > 0) {
    const label = `посчитано за ${f.seconds < 10 ? f.seconds.toFixed(1).replace('.', ',') : Math.round(f.seconds)} сек`;
    ctx.font = '600 30px Inter, system-ui, sans-serif';
    const tw = ctx.measureText(label).width;
    const bw = tw + 56;
    const bx = W / 2 - bw / 2;
    const by = IMG_Y + IMG_S - 74;
    ctx.fillStyle = 'rgba(11,15,18,0.82)';
    roundRect(ctx, bx, by, bw, 56, 28);
    ctx.fill();
    ctx.strokeStyle = 'rgba(16,229,142,0.4)';
    ctx.lineWidth = 1.5;
    roundRect(ctx, bx, by, bw, 56, 28);
    ctx.stroke();
    ctx.fillStyle = EMERALD;
    ctx.fillText(label, W / 2, by + 38);
  }
  }

  // ── Название блюда ──
  // Названия бывают длинные («Лепёшка с ветчиной, помидором и салатом»),
  // поэтому кегль ужимается под ширину, а не обрезается многоточием:
  // обрезанное название на карточке, которую понесут наружу, — это брак.
  let y = hasPhoto ? IMG_Y + IMG_S + 80 : SAFE_TOP + 150;
  ctx.fillStyle = INK;
  let titleSize = 44;
  const maxTitleW = W - PAD * 2;
  do {
    ctx.font = `600 ${titleSize}px Inter, system-ui, sans-serif`;
    if (ctx.measureText(f.title).width <= maxTitleW) break;
    titleSize -= 2;
  } while (titleSize > 30);
  ctx.fillText(f.title, W / 2, y);

  // ── Калории крупно ──
  y += 130;
  ctx.fillStyle = EMERALD;
  ctx.font = '700 130px "Space Grotesk", Inter, system-ui, sans-serif';
  ctx.shadowColor = 'rgba(16,229,142,0.4)';
  ctx.shadowBlur = 36;
  // Тильда перед числами (05.09.2026): распознавание по фото даёт ±15% по весу,
  // и голое «946 ккал» на публичной картинке заявляет точность, которой нет.
  // В приложении об этом сказано строкой «Оценка примерная», а карточка уходит
  // туда, где этой строки не будет — значит оговорка должна ехать с цифрой.
  ctx.fillText(`~${Math.round(f.calories)}`, W / 2, y);
  ctx.shadowBlur = 0;
  ctx.fillStyle = INK_MUTED;
  ctx.font = '500 32px Inter, system-ui, sans-serif';
  ctx.fillText('ккал', W / 2, y + 46);

  // ── БЖУ тремя колонками, цвета как в приложении ──
  y += 120;
  const cols: [string, number, string][] = [
    ['Белки', f.protein, EMERALD],
    ['Жиры', f.fat, WARN],
    ['Углеводы', f.carbs, SKY],
  ];
  const colW = (W - PAD * 2) / 3;
  cols.forEach(([label, val, color], i) => {
    const cx = PAD + colW * i + colW / 2;
    ctx.fillStyle = INK_DIM;
    ctx.font = '400 26px Inter, system-ui, sans-serif';
    ctx.fillText(label, cx, y);
    ctx.fillStyle = color;
    ctx.font = '600 44px "Space Grotesk", Inter, system-ui, sans-serif';
    ctx.fillText(`~${Math.round(val)}г`, cx, y + 52);
  });

  // ── Витамины и минералы: то, чего не показывает никто ──
  //
  // Здесь стояла строка-вывод по БЖУ, и Слава её забраковал: после перевода
  // на проценты она пересказывала те же БЖУ, что нарисованы выше. Витамины —
  // единственное на карточке, чего нет у конкурентов, и ради чего её сохранят.
  //
  // На карточке С ФОТО их может не быть: до сохранения досчёт не отработал
  // (FAST-режим считает шесть полей ради скорости). Нет — просто линия ближе,
  // шаг задан в каждой ветке явно, раскладка это переживает.
  const micros = f.micros ?? [];

  if (!hasPhoto) {
    // ── Без фото: витамины крупным списком, они здесь главные ──
    // Состав рисуем ДАЖЕ когда ни один нутриент не дотянул до порога:
    // без него карточка была бы просто «Обед, ~740 ккал» и пустота,
    // а так остаётся понятно, из чего приём состоял.
    y += 130;
    const rowH = 88;
    ctx.textAlign = 'left';
    micros.forEach((m, i) => {
      const ry = y + i * rowH;
      ctx.fillStyle = 'rgba(255,255,255,0.04)';
      roundRect(ctx, PAD, ry - 44, W - PAD * 2, 72, 20);
      ctx.fill();
      ctx.fillStyle = INK;
      ctx.font = '500 34px Inter, system-ui, sans-serif';
      ctx.fillText(`${m.emoji}  ${m.label}`, PAD + 32, ry + 8);
      ctx.textAlign = 'right';
      ctx.fillStyle = m.percent >= 50 ? EMERALD : INK_MUTED;
      ctx.font = '600 40px "Space Grotesk", Inter, system-ui, sans-serif';
      ctx.fillText(`~${m.percent}%`, W - PAD - 32, ry + 10);
      ctx.textAlign = 'left';
    });
    ctx.textAlign = 'center';
    y += micros.length * rowH + 6;
    if (micros.length) {
      ctx.fillStyle = INK_DIM;
      ctx.font = '400 24px Inter, system-ui, sans-serif';
      ctx.fillText('от дневной нормы', W / 2, y);
    }

    // Состав вместо фото: без него непонятно, из чего эти проценты набрались.
    if (f.composition) {
      y += 74;
      ctx.fillStyle = INK_MUTED;
      ctx.font = '400 26px Inter, system-ui, sans-serif';
      for (const line of wrapText(ctx, f.composition, W - PAD * 2).slice(0, 2)) {
        ctx.fillText(line, W / 2, y);
        y += 38;
      }
      y += 34;
    } else {
      y += 92;
    }
  } else if (micros.length) {
    // ── С фото: одна строка, места больше нет ──
    y += 112;
    const parts = micros.map((m) => `${m.emoji} ${m.label} ~${m.percent}%`);
    // Ужимаем шрифт, а не переносим: перенос менял бы ВЫСОТУ раскладки,
    // а её непредсказуемость уже один раз обрезала низ карточки.
    const maxW = W - PAD * 2;
    let size = 28;
    let line = parts.join('  ·  ');
    ctx.font = `500 ${size}px Inter, system-ui, sans-serif`;
    // Сначала выкидываем третий чип, и только потом мельчим: три подписи
    // шрифтом 22px читаются в сторис хуже, чем две нормальным.
    while (ctx.measureText(line).width > maxW && parts.length > 1) {
      parts.pop();
      line = parts.join('  ·  ');
    }
    while (ctx.measureText(line).width > maxW && size > 22) {
      size -= 2;
      ctx.font = `500 ${size}px Inter, system-ui, sans-serif`;
    }
    ctx.fillStyle = INK;
    ctx.fillText(line, W / 2, y);
    // Подпись обязательна: «~45%» без неё читается как «45% чего?».
    y += 40;
    ctx.fillStyle = INK_DIM;
    ctx.font = '400 22px Inter, system-ui, sans-serif';
    ctx.fillText('от дневной нормы', W / 2, y);
    // Посчитано по пикселям, а не на глаз: чипы 1442, подпись 1482,
    // линия 1516, ссылка 1610 при границе 1632.
    y += 34;
  } else {
    y += 110;
  }

  // ── Подвал: продолжает ТОТ ЖЕ поток, а не считается от низа холста.
  // Именно развязка «сверху» и «снизу» и наложила подвал на БЖУ. ──
  ctx.strokeStyle = 'rgba(255,255,255,0.14)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(PAD, y); ctx.lineTo(W - PAD, y); ctx.stroke();
  y += 44;
  ctx.fillStyle = INK_MUTED;
  ctx.font = '400 28px Inter, system-ui, sans-serif';
  ctx.fillText('Сфотографировал — и всё посчитано', W / 2, y);
  y += 50;
  ctx.fillStyle = EMERALD;
  ctx.font = '600 34px Inter, system-ui, sans-serif';
  ctx.fillText(`t.me/${BOT_USERNAME}`, W / 2, y);

  // Страховка от повторения бага: если раскладка снова перестанет помещаться,
  // это будет видно в консоли, а не только глазами на готовой картинке.
  // Проверяем не край холста, а безопасную зону сторис: содержимое ниже 85%
  // высоты прячется под интерфейсом Telegram и с виду просто пропадает.
  const SAFE_BOTTOM = Math.round(H * 0.85);
  if (y > SAFE_BOTTOM) console.warn('[recognitionCard] низ уходит под интерфейс сторис:', y, '>', SAFE_BOTTOM);

  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas пуст'))), 'image/png', 0.95),
  );
}

export type ShareChannel = 'story' | 'chat' | 'image';

export async function shareRecognition(
  channel: ShareChannel,
  f: RecognitionFacts,
  ctx: { userId: string; name?: string | null; refCode?: string | null },
): Promise<void> {
  const blob = await renderRecognitionCard(f, ctx);
  const link = inviteLink(ctx.refCode);
  const text = `${f.title} — ${Math.round(f.calories)} ккал, посчитано по фото`;

  if (channel === 'story') {
    const url = await uploadCard(blob, ctx.userId, 'recognition');
    tg().shareToStory(url, {
      text: `${text} — ${link}`,
      widget_link: { url: link, name: 'Slava Health' },
    });
  } else if (channel === 'chat') {
    // Раньше тут всегда уходил текст со ссылкой — теперь сама карточка блюда (07.10.2026)
    await shareCardWithFriend(blob, {
      userId: ctx.userId,
      key: 'recognition',
      caption: text,
      link,
      fileName: 'slava-raspoznavanie.png',
    });
  } else {
    const file = 'slava-raspoznavanie.png';
    if (!(await shareImageFile(blob, file, `${text} ${link}`))) downloadImage(blob, file);
  }

  supabase.from('share_events').insert({ user_id: ctx.userId, achievement_id: null, channel }).then(() => {});
}
